import { useEffect, useRef, useState } from 'react';

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

/**
 * Animates a displayed number toward `value` whenever it changes, instead of
 * snapping — used for the trade-fill number and the portfolio hero balance.
 * Pass `initial` to force an animation in from that number on mount (the fill
 * moment always counts up from 0); omitted, the first render shows `value`
 * as-is and only later changes animate (the portfolio balance shouldn't
 * count up from zero every time the page loads).
 */
export default function useCountUp(value, { duration = 650, initial } = {}) {
  const startValue = initial !== undefined ? initial : value;
  const [display, setDisplay] = useState(startValue);
  const prevRef = useRef(startValue);
  const frameRef = useRef(null);

  useEffect(() => {
    const from = prevRef.current;
    const to = value;
    prevRef.current = value;
    if (from === to || from === null || from === undefined || Number.isNaN(from)) {
      setDisplay(to);
      return undefined;
    }
    if (prefersReducedMotion()) {
      setDisplay(to);
      return undefined;
    }
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - t) ** 3;
      setDisplay(from + (to - from) * eased);
      if (t < 1) frameRef.current = requestAnimationFrame(tick);
      else setDisplay(to);
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);

  return display;
}
